import { useEffect, useState, useRef, useCallback } from "react";
import ArrowLeft from '../../assets/svg/arrow-left.svg';
import ArrowRight from '../../assets/svg/arrow-right.svg';
import Attachement from "../../assets/svg/attachment.svg";
import EmojiButton from "../../assets/svg/emojibutton.svg";
import EmojiTray from "../emoji/emoji";
import { useAuth } from "../../context/auth/auth-context";
import Tooltip from "../tooltip";
import { customEmojis } from "../../constants/dropdown";
import { emojiAttributes } from "../../constants/constants";

interface ImageItem {
    id: string;
    url: string;
    media: any;
    groupedEmoticons: any;
    profileImage: string;
}

interface CarouselProps {
    images: ImageItem[];
    interval?: number;
    parentClassName?: string;
    imageClassName?: string;
    loadingClassName?: string;
    carouselAttachementModal: any;
    setCarouselAttachementModal: any;
    likesModal: any;
    setLikesModal: any;
    openTooltip: boolean;
    setOpenTooltip: (value: boolean) => void;
    handleEmojiClick: (imageId: number, emoji: any) => void;
    setShowEmojiPicker?: any;
    showEmojiPicker?: boolean;
}

function AwardsCarousel({
    images,
    interval = 3000,
    parentClassName,
    imageClassName,
    loadingClassName,
    carouselAttachementModal,
    setCarouselAttachementModal,
    likesModal,
    setLikesModal,
    openTooltip,
    setOpenTooltip,
    handleEmojiClick,
    showEmojiPicker,
    setShowEmojiPicker
}: CarouselProps) {
    const { auth } = useAuth();
    const { user } = auth;

    const [activeIndex, setActiveIndex] = useState(0);
    const [cachedImages, setCachedImages] = useState<Map<string, string>>(new Map());
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const [forwardBackward, setForwardBackward] = useState<boolean>(true);
    const [manualChangeTrigger, setManualChangeTrigger] = useState(0);
    const [thumbStartIndex, setThumbStartIndex] = useState(0);
    const emojiTrayRef = useRef<HTMLDivElement>(null);
    const emojiButtonRef = useRef<HTMLDivElement>(null);

    const thumbnailsToShow = 3;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                showEmojiPicker &&
                emojiTrayRef.current &&
                emojiButtonRef.current &&
                !emojiTrayRef.current.contains(event.target as Node) &&
                !emojiButtonRef.current.contains(event.target as Node)
            ) {
                setShowEmojiPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cache images
    useEffect(() => {
        const cache = new Map<string, string>();
        images.forEach(({ url, profileImage }) => {
            [url, profileImage].forEach((imgUrl) => {
                if (imgUrl && !cache.has(imgUrl)) {
                    const img = new Image();
                    img.src = imgUrl;
                    img.onload = () => {
                        cache.set(imgUrl, imgUrl);
                        setCachedImages(new Map(cache));
                    };
                }
            });
        });
    }, [images]);

    useEffect(() => {
        if (carouselAttachementModal?.visibility || showEmojiPicker || likesModal?.visibility || !openTooltip) {
            setForwardBackward(false);
        } else {
            setForwardBackward(true);
        }
    }, [carouselAttachementModal, showEmojiPicker, likesModal, openTooltip]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (carouselAttachementModal?.visibility) return;
            if (event.key === "ArrowRight") handleNext();
            else if (event.key === "ArrowLeft") handlePrevious();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [activeIndex, carouselAttachementModal]);   // eslint-disable-line react-hooks/exhaustive-deps 

    // Unified auto-forward effect
    useEffect(() => {
        if (!forwardBackward) {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        timerRef.current = setInterval(() => {
            setActiveIndex((prevIndex) => (prevIndex + 1) % images?.length);
        }, interval);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [forwardBackward, interval, images?.length, manualChangeTrigger]);  // Include manualChangeTrigger

    const tooltipContent = useCallback((data: any[], emoticon: string, groupedEmoticons: any) => {
        const maxDisplay = 5;

        // Prioritize current user
        const sortedData = [...(data || [])].sort((a, b) => {
            if (a?.id === user?.id) return -1;
            if (b?.id === user?.id) return 1;
            return 0;
        });

        const displayData = sortedData.slice(0, maxDisplay);
        const remainingDataLength = sortedData.length > maxDisplay ? sortedData.length - maxDisplay : 0;

        return (
            <div className="relative"
            >
                <div
                    style={{ boxShadow: "0px 4px 11px 0px rgba(0, 0, 0, 0.25)" }}
                    className="flex flex-row items-start bg-white text-black text-xs rounded-md max-w-[200px] min-w-[200px] max-h-[200px] min-h-[50px] p-2 overflow-y-auto break-words  normal-case"
                >
                    <div className="w-[15%] ml-1 p-1 flex-shrink-0">
                        <img src={customEmojis.find((emoji) => emoji.unified === emoticon)?.image} alt={emoticon} className="w-5 h-5" />
                    </div>
                    <div className="w-[85%] p-1 break-words whitespace-normal">
                        {displayData.map((item: any, index: number) => {
                            const sameUser = item?.id === user?.id ? "You" : item?.name;
                            return (
                                <span key={index} className="normal-case">
                                    {sameUser}
                                    {index < displayData.length - 1 ? ", " : ""}
                                </span>
                            );
                        })}
                        {remainingDataLength > 0 && (
                            <span className="normal-case">
                                <span>{` and `}</span>
                                <span className="underline cursor-pointer"
                                    onClick={() => {
                                        setLikesModal({ visibility: true, data, groupedEmoticons, activeTab: emoticon });
                                    }}
                                >
                                    {`${remainingDataLength} other${remainingDataLength > 1 ? 's' : ''}`}
                                </span>
                            </span>
                        )}
                        <span className="lowercase">{` reacted with :${emojiAttributes[emoticon]}`}</span>
                    </div>
                </div>

                {/* Arrow */}
                <div className="absolute left-[20px] -bottom-[4px] w-3 h-3 bg-white rotate-45 "></div>
            </div>
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    const handlePrevious = () => {
        setActiveIndex((prevIndex) => {
            const nextIndex = prevIndex === 0 ? images.length - 1 : prevIndex - 1;

            // If wrapped to last, reset thumbnail tray to last window
            if (prevIndex === 0) {
                setThumbStartIndex(Math.max(images.length - thumbnailsToShow, 0));
            } else if (nextIndex < thumbStartIndex) {
                setThumbStartIndex(nextIndex);
            }

            setManualChangeTrigger((prev) => prev + 1);
            return nextIndex;
        });
    };

    const handleNext = () => {
        setActiveIndex((prevIndex) => {
            const nextIndex = (prevIndex + 1) % images.length;

            // If wrapped to 0, reset thumbnail tray
            if (nextIndex === 0) {
                setThumbStartIndex(0);
            } else if (nextIndex >= thumbStartIndex + thumbnailsToShow) {
                setThumbStartIndex(nextIndex - thumbnailsToShow + 1);
            }

            setManualChangeTrigger((prev) => prev + 1);
            return nextIndex;
        });
    };

    const handleViewAttachemnt = useCallback((data: any) => {
        if (Array.isArray(data) && data?.length > 0) {
            const imageList = data?.map((item: any) => item?.url) || [];
            setCarouselAttachementModal({ visibility: true, data: imageList });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    const handleThumbScrollLeft = () => {
        setThumbStartIndex((prev) => Math.max(0, prev - 1));
    };

    const handleThumbScrollRight = () => {
        setThumbStartIndex((prev) => Math.min(images.length - thumbnailsToShow, prev + 1));
    };

    const activeImage = images[activeIndex];

    return (
        <div className="flex flex-col items-center relative">
            {/* Main Image */}
            <div
                className={`w-full relative max-w-[450px] ${parentClassName}`}
                onMouseEnter={() => setForwardBackward(false)}
                onMouseLeave={() => setForwardBackward(true)}
            >
                {cachedImages.has(activeImage.url) ? (
                    <img
                        src={cachedImages.get(activeImage.url)}
                        alt={`${activeIndex + 1}`}
                        className={`rounded-lg w-full h-80 object-contain ${imageClassName}`}
                    />
                ) : (
                    <div className={`w-full h-64 flex items-center justify-center bg-gray-200 rounded-lg ${loadingClassName}`}>
                        Loading...
                    </div>
                )}
            </div>

            {/* Arrows */}
            {images.length > 1 && (
                <>
                    <button
                        onClick={handlePrevious}
                        className="absolute left-2 top-[35%] bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 focus:outline-none"
                    >
                        <img src={ArrowLeft} alt="Previous" />
                    </button>
                    <button
                        onClick={handleNext}
                        className="absolute right-2 top-[35%] bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 focus:outline-none"
                    >
                        <img src={ArrowRight} alt="Next" />
                    </button>
                </>
            )}

            {/* Emoji & Attachment */}
            <div className="flex flex-row items-center justify-between w-full mt-2">
                <div className="flex items-center space-x-1">
                    <div
                        className="relative flex items-center p-1 border border-feildBorder rounded-basicRadius cursor-pointer"
                        ref={emojiButtonRef}
                    >
                        <img
                            src={EmojiButton}
                            alt="EmojiButton"
                            className="h-5 w-5"
                            onClick={() => setShowEmojiPicker((prev: boolean) => !prev)}
                        />
                        {showEmojiPicker && (
                            <div ref={emojiTrayRef}>
                                <EmojiTray
                                    onEmojiClick={(emoji: any) => {
                                        const imageId = activeImage.id;
                                        handleEmojiClick(Number(imageId), emoji);
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-[5px] items-center max-w-[15rem]">
                        {Array.isArray(activeImage?.groupedEmoticons) && activeImage?.groupedEmoticons.map((objEmoji: any, index: number) => (
                            <Tooltip
                                isShowEmoji
                                key={index}
                                content={tooltipContent(objEmoji.users, objEmoji.emoticon, activeImage?.groupedEmoticons)}
                                className={`rounded-lg !bottom-[25px] ${objEmoji.count > 0 ? 'has-counter' : ''}`}
                            >
                                <div
                                    onMouseEnter={() => setOpenTooltip(false)}
                                    onMouseLeave={() => setOpenTooltip(true)}
                                    onClick={() => setLikesModal({ visibility: true, data: objEmoji.users, groupedEmoticons: activeImage?.groupedEmoticons, activeTab: objEmoji?.emoticon })}
                                    className="flex items-center gap-[5px] bg-whiteCol rounded-sm border border-red-500 px-2 py-1 shadow cursor-pointer">
                                    <img src={customEmojis.find((emoji) => emoji.unified === objEmoji.emoticon)?.image} alt={objEmoji.emoticon} className="w-4 h-4" />

                                    {objEmoji.count > 0 && (
                                        <span className="text-xs">{objEmoji.count}</span>
                                    )}
                                </div>
                            </Tooltip>
                        ))}
                    </div>
                </div>
                {activeImage.media && activeImage.media.length > 0 && (
                    <div
                        className="flex items-center space-x-1 p-1 border border-feildBorder rounded-basicRadius cursor-pointer"
                        onClick={() => handleViewAttachemnt(activeImage.media)}
                    >
                        <img src={Attachement} alt="Attachment" className="h-4 w-4" />
                        <span className="text-[12px] text-[#101820]">
                            {activeImage.media.length} Attachment{activeImage.media.length > 1 ? 's' : ''}
                        </span>
                    </div>
                )}
            </div>

            {/* Thumbnails */}

            {/* Thumbnails with Pagination */}
            <div className="flex items-center mt-4 gap-2">
                {/* Left Arrow */}
                <button
                    onClick={handleThumbScrollLeft}
                    disabled={thumbStartIndex === 0}
                    className="p-1 disabled:opacity-30"
                >
                    <img src={ArrowLeft} alt="Scroll Left" className="w-4 h-4" />
                </button>

                {/* Thumbnails */}
                <div className="flex gap-1">
                    {images.slice(thumbStartIndex, thumbStartIndex + thumbnailsToShow).map(({ id, url, profileImage }, index) => {
                        const actualIndex = thumbStartIndex + index;
                        return (
                            <button
                                key={id}
                                onClick={() => {
                                    setActiveIndex(actualIndex);
                                    setManualChangeTrigger((prev) => prev + 1);
                                }}
                                onMouseEnter={() => setForwardBackward(false)}
                                onMouseLeave={() => setForwardBackward(true)}
                                className="rounded-md hover:opacity-75"
                            >
                                {cachedImages.has(profileImage) ? (
                                    <img
                                        src={cachedImages.get(profileImage)}
                                        alt={`Thumbnail ${actualIndex + 1}`}
                                        className={`object-cover rounded-lg ${actualIndex === activeIndex
                                            ? 'w-12 h-12 border-2 border-primary'
                                            : 'w-10 h-10'
                                            }`}
                                    />
                                ) : (
                                    <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Right Arrow */}
                <button
                    onClick={handleThumbScrollRight}
                    disabled={thumbStartIndex + thumbnailsToShow >= images.length}
                    className="p-1 disabled:opacity-30"
                >
                    <img src={ArrowRight} alt="Scroll Right" className="w-4 h-4" />
                </button>
            </div>

        </div >
    );
}

export default AwardsCarousel;